import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Chip,
  Paper,
  Divider,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import SchoolIcon from "@mui/icons-material/School";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import {
  useTutorialConfig,
  TutorialConfig,
  TutorialStepConfig,
  DEFAULT_TUTORIAL_CONFIG,
} from "../hooks/useTutorialConfig";

// Elementos disponibles para enfocar el tutorial
const TARGET_ELEMENTS = [
  { id: "#tour-step-size", name: "🍕 Selector de Tamaño de Pizza", defaultSide: "bottom" },
  { id: ".progress-bar", name: "📍 Barra de Progreso Superior", defaultSide: "bottom" },
  { id: "#tour-step-slice", name: "✂️ Opciones de División (Entera / Mitades)", defaultSide: "bottom" },
  { id: "#tour-step-flavor", name: "🎨 Selector de Sabores e Ingredientes", defaultSide: "bottom" },
  { id: ".cart-footer-wrapper", name: "🛒 Botón Flotante 'Agregar al Carrito'", defaultSide: "top" },
  { id: "#tour-step-additions", name: "🥤 Sección de Extras, Bordes y Bebidas", defaultSide: "bottom" },
  { id: "#tour-step-summary", name: "✅ Resumen y Confirmación de Pedido", defaultSide: "top" },
];

export default function TutorialPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { tutorialConfig, loading, updateTutorialConfig } = useTutorialConfig(restaurantId);

  const [localConfig, setLocalConfig] = useState<TutorialConfig>(DEFAULT_TUTORIAL_CONFIG);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [previewStepIdx, setPreviewStepIdx] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (!loading) {
      setLocalConfig(tutorialConfig);
    }
  }, [tutorialConfig, loading]);

  const stepKeys: Array<"step0" | "step1" | "step2" | "step3"> = ["step0", "step1", "step2", "step3"];
  const currentStepKey = stepKeys[activeTab];
  const currentStepItems = localConfig[currentStepKey] || [];

  // Reset preview index when tab changes
  useEffect(() => {
    setPreviewStepIdx(0);
  }, [activeTab]);

  const handleSave = async () => {
    setSaving(true);
    await updateTutorialConfig(localConfig);
    setSaving(false);
    setSnackbarOpen(true);
  };

  const handleResetDefaults = async () => {
    if (window.confirm("¿Restablecer todas las cajas de información a la configuración original por defecto?")) {
      setLocalConfig(DEFAULT_TUTORIAL_CONFIG);
      await updateTutorialConfig(DEFAULT_TUTORIAL_CONFIG);
      setSnackbarOpen(true);
    }
  };

  const handleAddBox = () => {
    setLocalConfig((prev) => ({
      ...prev,
      [currentStepKey]: [
        ...prev[currentStepKey],
        {
          element: activeTab === 0 ? "#tour-step-size" : activeTab === 1 ? "#tour-step-slice" : activeTab === 2 ? "#tour-step-flavor" : "#tour-step-additions",
          title: "📌 Nueva Caja de Ayuda",
          description: "Escribe aquí la explicación para tus clientes...",
          side: "bottom",
          align: "center",
        },
      ],
    }));
    setPreviewStepIdx(currentStepItems.length);
  };

  const handleRemoveBox = (index: number) => {
    setLocalConfig((prev) => {
      const updated = prev[currentStepKey].filter((_, i) => i !== index);
      return {
        ...prev,
        [currentStepKey]: updated,
      };
    });
    setPreviewStepIdx(Math.max(0, index - 1));
  };

  const handleUpdateBox = (index: number, field: keyof TutorialStepConfig, value: string) => {
    setLocalConfig((prev) => {
      const updated = [...prev[currentStepKey]];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return {
        ...prev,
        [currentStepKey]: updated,
      };
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const activeItem = currentStepItems[previewStepIdx] || currentStepItems[0];
  const activeTargetId = activeItem?.element || "#tour-step-size";
  const targetObj = TARGET_ELEMENTS.find((t) => t.id === activeTargetId) || TARGET_ELEMENTS[0];

  return (
    <Box sx={{ maxWidth: 1280, mx: "auto", pb: 6 }}>
      {/* Cabecera Principal */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="primary.main" display="flex" alignItems="center" gap={1.5}>
            <SchoolIcon fontSize="large" /> Configurar Tutorial Guiado
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Agrega las cajas de información que desees para cada pantalla y observa la vista previa fiel a la app
          </Typography>
        </Box>

        <Stack direction="row" gap={1.5}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RestoreIcon />}
            onClick={handleResetDefaults}
          >
            Restablecer Valores
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ px: 3, fontWeight: 800, borderRadius: 2 }}
          >
            Guardar Cambios
          </Button>
        </Stack>
      </Stack>

      {/* Switch General */}
      <Card variant="outlined" sx={{ borderRadius: 3, mb: 4, bgcolor: localConfig.enabled ? "#f0fdf4" : "#fef2f2", borderColor: localConfig.enabled ? "#bbf7d0" : "#fecaca" }}>
        <CardContent sx={{ p: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              label={localConfig.enabled ? "ACTIVADO" : "DESACTIVADO"}
              color={localConfig.enabled ? "success" : "error"}
              sx={{ fontWeight: 800 }}
            />
            <Typography variant="subtitle1" fontWeight={700} color={localConfig.enabled ? "success.dark" : "error.dark"}>
              {localConfig.enabled ? "El tutorial interactivo se mostrará a los nuevos clientes" : "El tutorial interactivo está oculto en la tienda"}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={localConfig.enabled}
                onChange={(e) => setLocalConfig({ ...localConfig, enabled: e.target.checked })}
                color="success"
              />
            }
            label=""
          />
        </CardContent>
      </Card>

      {/* Pestañas de Pantallas del Pedido */}
      <Paper variant="outlined" sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="🍕 Paso 1: Tamaño" sx={{ fontWeight: 800, py: 2 }} />
          <Tab label="✂️ Paso 2: División" sx={{ fontWeight: 800, py: 2 }} />
          <Tab label="🎨 Paso 3: Sabores" sx={{ fontWeight: 800, py: 2 }} />
          <Tab label="🛒 Paso 4: Carrito / Extras" sx={{ fontWeight: 800, py: 2 }} />
        </Tabs>
      </Paper>

      {/* LAYOUT DOS COLUMNAS: EDITOR (IZQ) Y SIMULADOR RÉPLICA EXACTA (DER) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 390px" }, gap: 4 }}>
        
        {/* COLUMNA IZQUIERDA: CREADOR / EDITOR DE CAJAS DE INFORMACIÓN */}
        <Box display="flex" flexDirection="column" gap={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={800} color="text.primary">
              Cajas de Información ({currentStepItems.length})
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddCircleIcon />}
              onClick={handleAddBox}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              + Agregar Caja de Información
            </Button>
          </Box>

          {currentStepItems.length === 0 ? (
            <Alert severity="warning" sx={{ borderRadius: 3 }}>
              No hay cajas de información en este paso. ¡Haz clic en "+ Agregar Caja de Información" para crear una!
            </Alert>
          ) : (
            currentStepItems.map((item, idx) => {
              const isSelectedForPreview = idx === previewStepIdx;
              return (
                <Card
                  key={idx}
                  variant="outlined"
                  onClick={() => setPreviewStepIdx(idx)}
                  sx={{
                    borderRadius: 3,
                    borderWidth: isSelectedForPreview ? 2 : 1,
                    borderColor: isSelectedForPreview ? "primary.main" : "divider",
                    boxShadow: isSelectedForPreview ? "0 4px 14px rgba(255, 107, 26, 0.15)" : "none",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2.5}>
                      {/* Cabecera de Tarjeta */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Chip
                            label={`Caja #${idx + 1}`}
                            color={isSelectedForPreview ? "primary" : "default"}
                            size="small"
                            sx={{ fontWeight: 800 }}
                          />
                          {isSelectedForPreview && (
                            <Chip label="👁️ Viendo en Simulador" size="small" color="success" variant="outlined" />
                          )}
                        </Stack>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveBox(idx);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>

                      {/* Selector descriptivo del elemento al que apunta */}
                      <FormControl fullWidth size="small">
                        <InputLabel>Elemento de la App al que apunta</InputLabel>
                        <Select
                          value={item.element || TARGET_ELEMENTS[0].id}
                          label="Elemento de la App al que apunta"
                          onChange={(e) => handleUpdateBox(idx, "element", e.target.value)}
                        >
                          {TARGET_ELEMENTS.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                              {t.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Título del Mensaje */}
                      <TextField
                        label="Título del Mensaje"
                        fullWidth
                        size="small"
                        value={item.title}
                        onChange={(e) => handleUpdateBox(idx, "title", e.target.value)}
                        placeholder="Ej: 🍕 Paso 1: Elije tu Tamaño"
                      />

                      {/* Descripción Explicativa */}
                      <TextField
                        label="Mensaje o Explicación para el Cliente"
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        value={item.description}
                        onChange={(e) => handleUpdateBox(idx, "description", e.target.value)}
                        placeholder="Ej: Empieza eligiendo el tamaño de la pizza que deseas..."
                      />

                      {/* Posición del mensaje flotante */}
                      <FormControl size="small" sx={{ maxWidth: 200 }}>
                        <InputLabel>Posición del Mensaje</InputLabel>
                        <Select
                          value={item.side || "bottom"}
                          label="Posición del Mensaje"
                          onChange={(e) => handleUpdateBox(idx, "side", e.target.value as any)}
                        >
                          <MenuItem value="bottom">Abajo del Elemento (Bottom)</MenuItem>
                          <MenuItem value="top">Arriba del Elemento (Top)</MenuItem>
                          <MenuItem value="left">A la Izquierda (Left)</MenuItem>
                          <MenuItem value="right">A la Derecha (Right)</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>

        {/* COLUMNA DERECHA: SIMULADOR VISUAL DE TELÉFONO RÉPLICA EXACTA DE THOMAS PIZZA */}
        <Box sx={{ position: { lg: "sticky" }, top: 20, height: "fit-content" }}>
          <Paper
            elevation={6}
            sx={{
              borderRadius: 7,
              p: 2,
              bgcolor: "#0f172a",
              color: "#ffffff",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.4)",
              border: "5px solid #334155",
            }}
          >
            {/* Header Celular Notch */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} px={1}>
              <Stack direction="row" alignItems="center" gap={1}>
                <SmartphoneIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={800} color="#f8fafc" sx={{ fontSize: "0.85rem" }}>
                  App Thomas Pizza Real
                </Typography>
              </Stack>
              <Chip label="SIMULADOR EN VIVO" size="small" color="error" sx={{ fontWeight: 800, height: 18, fontSize: "0.6rem" }} />
            </Stack>

            {/* PANTALLA MÓVIL EXACTA DE THOMAS PIZZA */}
            <Box
              sx={{
                bgcolor: "#fafafa",
                color: "#1e293b",
                borderRadius: 5,
                p: 2,
                minHeight: 520,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                overflow: "hidden",
                border: "1px solid #cbd5e1",
                fontFamily: "sans-serif",
              }}
            >
              {/* HEADER REAL DE THOMAS PIZZA */}
              <Box sx={{ pb: 1, borderBottom: "1px solid #e2e8f0" }}>
                <Typography variant="subtitle2" fontWeight={900} color="#ff6b1a" align="center" letterSpacing={1}>
                  🍕 THOMAS PIZZA
                </Typography>
              </Box>

              {/* BARRA DE PROGRESO REAL DE THOMAS PIZZA */}
              <Box
                className="progress-bar"
                sx={{
                  py: 1,
                  px: 1.5,
                  my: 1,
                  borderRadius: 2,
                  bgcolor: "#fff5f0",
                  border: activeTargetId === ".progress-bar" ? "2px solid #ef4444" : "1px solid #ffedd5",
                  boxShadow: activeTargetId === ".progress-bar" ? "0 0 12px rgba(239,68,68,0.5)" : "none",
                  transition: "all 0.3s ease",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  {[
                    { num: "1", label: "Tamaño", step: 0 },
                    { num: "2", label: "División", step: 1 },
                    { num: "3", label: "Sabores", step: 2 },
                    { num: "4", label: "Carrito", step: 3 },
                  ].map((s) => (
                    <Box key={s.num} textAlign="center">
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          bgcolor: activeTab >= s.step ? "#ff6b1a" : "#cbd5e1",
                          color: "#fff",
                          fontSize: "0.65rem",
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                        }}
                      >
                        {s.num}
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: activeTab === s.step ? 800 : 500, color: activeTab === s.step ? "#ff6b1a" : "#64748b" }}>
                        {s.label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* CONTENIDO RÉPLICA EXACTA DE CADA PASO */}
              <Box sx={{ flex: 1, py: 1 }}>
                
                {/* MOCKUP PASO 0: SELECCIÓN DE TAMAÑO */}
                {activeTab === 0 && (
                  <Box
                    id="tour-step-size"
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: activeTargetId === "#tour-step-size" ? "2.5px solid #ef4444" : "1px solid #e2e8f0",
                      boxShadow: activeTargetId === "#tour-step-size" ? "0 0 16px rgba(239,68,68,0.4)" : "none",
                      bgcolor: "#ffffff",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} color="#ff6b1a" display="block" mb={1}>
                      🍕 Selecciona el Tamaño de la Pizza
                    </Typography>

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                      {[
                        { name: "Personal 6\"", desc: "4 porciones", price: "$16.000" },
                        { name: "Mediana 10\"", desc: "8 porciones", price: "$32.000" },
                        { name: "Grande 12\"", desc: "10 porciones", price: "$45.000" },
                        { name: "Familiar 14\"", desc: "12 porciones", price: "$58.000" },
                      ].map((item, i) => (
                        <Paper
                          key={i}
                          variant="outlined"
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            borderColor: i === 2 ? "#ff6b1a" : "#e2e8f0",
                            bgcolor: i === 2 ? "#fff7ed" : "#fff",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="caption" fontWeight={800} display="block" sx={{ fontSize: "0.7rem" }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.6rem" }}>
                            {item.desc}
                          </Typography>
                          <Typography variant="caption" fontWeight={800} color="#ff6b1a" sx={{ fontSize: "0.65rem" }}>
                            {item.price}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* MOCKUP PASO 1: SELECCIÓN DE DIVISIÓN */}
                {activeTab === 1 && (
                  <Box
                    id="tour-step-slice"
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: activeTargetId === "#tour-step-slice" ? "2.5px solid #ef4444" : "1px solid #e2e8f0",
                      boxShadow: activeTargetId === "#tour-step-slice" ? "0 0 16px rgba(239,68,68,0.4)" : "none",
                      bgcolor: "#ffffff",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} color="#ff6b1a" display="block" mb={1}>
                      ✂️ ¿Cómo deseas tu pizza?
                    </Typography>

                    <Stack spacing={1}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: "#ff6b1a", bgcolor: "#fff7ed" }}>
                        <Typography variant="caption" fontWeight={800} display="block" sx={{ fontSize: "0.75rem" }}>
                          🍕 Pizza Entera (1 Sabor)
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          Disfruta un solo sabor delicioso en toda la pizza
                        </Typography>
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: "#cbd5e1", bgcolor: "#fff" }}>
                        <Typography variant="caption" fontWeight={800} display="block" sx={{ fontSize: "0.75rem" }}>
                          🌗 Mitad y Mitad (2 Sabores)
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          Combina 2 sabores distintos en cada mitad
                        </Typography>
                      </Paper>
                    </Stack>
                  </Box>
                )}

                {/* MOCKUP PASO 2: SELECCIÓN DE SABORES */}
                {activeTab === 2 && (
                  <Box
                    id="tour-step-flavor"
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: activeTargetId === "#tour-step-flavor" ? "2.5px solid #ef4444" : "1px solid #e2e8f0",
                      boxShadow: activeTargetId === "#tour-step-flavor" ? "0 0 16px rgba(239,68,68,0.4)" : "none",
                      bgcolor: "#ffffff",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} color="#ff6b1a" display="block" mb={1}>
                      🎨 Elige tus Sabores Favoritos
                    </Typography>

                    <Stack direction="row" gap={0.8} flexWrap="wrap" mb={1}>
                      {["Hawayana 🍍", "Pepperoni 🍕", "Pollo Champiñón 🍗", "Mexicana 🌶️"].map((flavor, i) => (
                        <Chip
                          key={i}
                          label={flavor}
                          size="small"
                          color={i === 0 ? "primary" : "default"}
                          sx={{ fontSize: "0.65rem", height: 22 }}
                        />
                      ))}
                    </Stack>

                    <Box
                      className="cart-footer-wrapper"
                      sx={{
                        p: 1,
                        mt: 1.5,
                        borderRadius: 2,
                        bgcolor: "#ff6b1a",
                        color: "#fff",
                        textAlign: "center",
                        border: activeTargetId === ".cart-footer-wrapper" ? "2.5px solid #ef4444" : "none",
                        boxShadow: activeTargetId === ".cart-footer-wrapper" ? "0 0 16px rgba(239,68,68,0.6)" : "none",
                      }}
                    >
                      <Typography variant="caption" fontWeight={800} sx={{ fontSize: "0.75rem" }}>
                        🛒 Agregar al Carrito — $45.000
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* MOCKUP PASO 3: EXTRAS Y CARRITO */}
                {activeTab === 3 && (
                  <Box
                    id="tour-step-additions"
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: activeTargetId === "#tour-step-additions" ? "2.5px solid #ef4444" : "1px solid #e2e8f0",
                      boxShadow: activeTargetId === "#tour-step-additions" ? "0 0 16px rgba(239,68,68,0.4)" : "none",
                      bgcolor: "#ffffff",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} color="#ff6b1a" display="block" mb={1}>
                      🥤 Extras, Bebidas y Entrega
                    </Typography>

                    <Stack direction="row" gap={1} mb={1}>
                      {["🛵 Domicilio", "🍽️ Mesa", "🛍️ Llevar"].map((type, i) => (
                        <Box key={i} sx={{ flex: 1, p: 0.5, textAlign: "center", border: "1px solid #e2e8f0", borderRadius: 1.5, fontSize: "0.6rem", fontWeight: 700 }}>
                          {type}
                        </Box>
                      ))}
                    </Stack>

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0.5, mb: 1 }}>
                      {["🥤 Gaseosas", "🍺 Cervezas", "🍰 Postres"].map((cat, i) => (
                        <Box key={i} sx={{ p: 0.5, border: "1px solid #cbd5e1", borderRadius: 1.5, textAlign: "center", fontSize: "0.6rem", fontWeight: 700 }}>
                          {cat}
                        </Box>
                      ))}
                    </Box>

                    <Box
                      id="tour-step-summary"
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: "#16a34a",
                        color: "#fff",
                        textAlign: "center",
                        border: activeTargetId === "#tour-step-summary" ? "2.5px solid #ef4444" : "none",
                        boxShadow: activeTargetId === "#tour-step-summary" ? "0 0 16px rgba(239,68,68,0.6)" : "none",
                      }}
                    >
                      <Typography variant="caption" fontWeight={800} sx={{ fontSize: "0.75rem" }}>
                        ✅ Confirmar Pedido
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* MENSAJE FLOTANTE EN VIVO DEL TUTORIAL (DRIVER.JS POPOVER CARD) */}
              {activeItem ? (
                <Paper
                  elevation={10}
                  sx={{
                    p: 1.8,
                    borderRadius: 3,
                    bgcolor: "#ffffff",
                    border: "2px solid #ef4444",
                    boxShadow: "0 12px 28px rgba(239,68,68,0.3)",
                    position: "relative",
                    zIndex: 10,
                    mt: 1,
                    animation: "fadeIn 0.3s ease",
                  }}
                >
                  <Stack spacing={0.8}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" fontWeight={800} color="#b91c1c" sx={{ fontSize: "0.8rem" }}>
                        {activeItem.title || "Sin título"}
                      </Typography>
                      <Chip
                        label={`Paso ${previewStepIdx + 1} de ${currentStepItems.length}`}
                        size="small"
                        sx={{ fontSize: "0.65rem", height: 18, bgcolor: "#fee2e2", color: "#991b1b", fontWeight: 800 }}
                      />
                    </Stack>

                    <Typography variant="body2" color="#334155" sx={{ fontSize: "0.75rem", lineHeight: 1.35 }}>
                      {activeItem.description || "Escribe el mensaje explicativo para el cliente..."}
                    </Typography>

                    <Divider sx={{ my: 0.5 }} />

                    {/* Botones Simulados de Driver.js */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Button
                        size="small"
                        disabled={previewStepIdx === 0}
                        onClick={() => setPreviewStepIdx((prev) => Math.max(0, prev - 1))}
                        sx={{ fontSize: "0.7rem", textTransform: "none", py: 0.2 }}
                      >
                        {localConfig.prevBtnText || "Atrás"}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => setPreviewStepIdx((prev) => (prev + 1) % currentStepItems.length)}
                        sx={{ fontSize: "0.7rem", textTransform: "none", py: 0.2, px: 1.2, fontWeight: 800, borderRadius: 1.5 }}
                      >
                        {previewStepIdx === currentStepItems.length - 1
                          ? localConfig.doneBtnText || "¡Entendido!"
                          : localConfig.nextBtnText || "Siguiente"}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ) : (
                <Box textAlign="center" py={2}>
                  <Typography variant="caption" color="text.secondary">No hay cajas activas para esta pantalla</Typography>
                </Box>
              )}

              {/* Controles de Navegación del Simulador */}
              <Stack direction="row" justifyContent="center" alignItems="center" gap={2} pt={1} borderTop="1px solid #e2e8f0">
                <IconButton
                  size="small"
                  disabled={previewStepIdx === 0}
                  onClick={() => setPreviewStepIdx((prev) => Math.max(0, prev - 1))}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" fontWeight={800} sx={{ fontSize: "0.7rem" }}>
                  Mensaje {previewStepIdx + 1} / {Math.max(1, currentStepItems.length)}
                </Typography>
                <IconButton
                  size="small"
                  disabled={previewStepIdx >= currentStepItems.length - 1}
                  onClick={() => setPreviewStepIdx((prev) => Math.min(currentStepItems.length - 1, prev + 1))}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Botón Flotante para Guardar Fácil */}
      <Box mt={4} display="flex" justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={saving ? <CircularProgress size={22} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ px: 6, py: 1.5, fontSize: "1.1rem", borderRadius: 3, fontWeight: 800, boxShadow: "0 4px 14px rgba(255, 107, 26, 0.4)" }}
        >
          Guardar Configuración
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message="¡Configuración del tutorial guardada exitosamente!"
      />
    </Box>
  );
}
